import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, InputLabel, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { get, post } from "utils/WebService";
import { avisarFichaAlterada } from "utils/fichaAlterada";
import PacoteJuridicoMigracao, { PACOTE_VAZIO, pacoteParaCampos, pacoteParaEnvio } from "views/umc/filaSolicitacoes/PacoteJuridicoMigracao";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const celulaAcoes = { verticalAlign: "top", pt: 1.5 };
const SITUACOES = [
    { valor: "ABERTAS", nome: "Com o Jurídico (a fazer)" },
    { valor: "ENVIADA", nome: "Com o suporte" },
    { valor: "CONCLUIDA", nome: "Concluídas" },
    { valor: "RECUSADA", nome: "Recusadas pelo Jurídico" },
    { valor: "TODAS", nome: "Todas" },
];

const listaDe = (res) => (Array.isArray(res) ? res : res?.list || res?.data || []);
const dataHora = (ms) => (ms ? new Date(ms).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
const normalizar = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const moeda = (v) => (v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

/**
 * Abre a ficha numa aba nova do navegador, só para consulta (FichaAvulsa), como a Análise de Atendimento: a aba nova
 * recebe o login e o tema desta (o App.js lê e apaga os temp_share_* ao abrir).
 */
const abrirFicha = (item) => {
    [["temp_share_user", "user"], ["temp_share_menus", "userMenus"], ["temp_share_tema", "tema"]].forEach(([destino, origem]) => {
        const valor = sessionStorage.getItem(origem);
        if (valor) localStorage.setItem(destino, valor);
    });
    window.open(`/system?view=ficha&devedor=${encodeURIComponent(item.idDevedor)}&ficha=${encodeURIComponent(item.ficha)}`, "_blank");
};

/** A situação de cada item, em texto e cor de chip. */
function situacaoDo(i) {
    if (i.situacao === "PREENCHER") {
        return i.motivoDevolucao ? { texto: "Devolvida pelo suporte", cor: "warning" } : { texto: "A preencher", cor: "info" };
    }
    if (i.situacao === "AGUARDANDO") return { texto: "Já migrada, sem os dados", cor: "info" };
    if (i.situacao === "ENVIADA") return { texto: "Com o suporte", cor: "secondary" };
    if (i.situacao === "RECUSADA") return { texto: "Recusada", cor: "error" };
    return { texto: "Concluída", cor: "success" };
}

/**
 * Migrações para o Jurídico (Cobrança, c090). Fluxo de 26/10/2026 (pedido da Aline, conforme a operação): quem tem a
 * permissão de pedir migração (c091) pede a migração para a carteira jurídica; o pedido vem para cá; o CobJur preenche os
 * dados do Jurídico (código novo, zerar os débitos, débito do processo, processo e Nº GCPJ), o negociador e o status e
 * envia ao suporte, que aprova na Fila e conclui a migração (ou devolve para cá, com o motivo); o CobJur também pode
 * recusar o pedido (volta para quem pediu). As fichas que já chegaram ao Jurídico sem os dados (migração direta, pedidos
 * antigos) o CobJur conclui aqui. Parado há N dias úteis (MIGRACAO_JURIDICO_DIAS_AVISO, padrão 2) fica destacado aqui e no
 * sino. Backend: /migracoes-juridico.
 */
export default function MigracoesJuridico() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [situacao, setSituacao] = useState("ABERTAS");
    const [busca, setBusca] = useState("");
    const [dados, setDados] = useState({ itens: [], diasAviso: 2 });
    const [carregando, setCarregando] = useState(false);
    const [aviso, setAviso] = useState({ aberto: false, ok: true, texto: "" });
    // Formulário dos dados do Jurídico
    const [aberta, setAberta] = useState(null);
    const [pacote, setPacote] = useState(PACOTE_VAZIO);
    const [negociadores, setNegociadores] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [usuarioId, setUsuarioId] = useState("");
    const [statusId, setStatusId] = useState("");
    const [observacao, setObservacao] = useState("");
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");
    // Recusa do pedido
    const [recusando, setRecusando] = useState(null);
    const [motivo, setMotivo] = useState("");

    const avisar = (ok, texto) => setAviso({ aberto: true, ok, texto });

    const carregar = useCallback(() => {
        setCarregando(true);
        get(`/migracoes-juridico/list?situacao=${situacao}`)
            .then(res => setDados(res || { itens: [] }))
            .catch(e => avisar(false, e.message || "Erro ao carregar as migrações."))
            .finally(() => setCarregando(false));
    }, [situacao]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        get("/status-ficha/list-all").then(res => setStatusFicha(listaDe(res))).catch(() => setStatusFicha([]));
    }, []);

    const itens = useMemo(() => {
        const termo = normalizar(busca.trim());
        if (!termo) return dados.itens || [];
        return (dados.itens || []).filter(i => [i.devedor, i.codigoCliente, i.cliente, `#${i.ficha}`, String(i.ficha)]
            .some(v => normalizar(v).includes(termo)));
    }, [dados, busca]);

    const assumir = (item) => {
        post(`/migracoes-juridico/${item.id}/assumir`, {})
            .then(() => { avisar(true, `Ficha #${item.ficha} com você.`); carregar(); })
            .catch(e => avisar(false, e.message || "Erro ao assumir."));
    };

    const abrirFormulario = (item) => {
        setErro(""); setPacote(PACOTE_VAZIO); setUsuarioId(""); setStatusId(""); setObservacao("");
        get(`/migracoes-juridico/${item.id}`)
            .then(det => {
                setAberta(det);
                // Devolvida pelo suporte: o formulário volta com o que foi enviado, para corrigir
                const enviado = det.enviado || {};
                if (enviado.pacote) setPacote(pacoteParaCampos(enviado.pacote));
                if (enviado.usuarioId) setUsuarioId(enviado.usuarioId);
                if (enviado.statusFichaId) setStatusId(enviado.statusFichaId);
                // Os negociadores das equipes da carteira jurídica de destino
                get(`/users/list-all-operators?carteiraId=${det.idCarteiraDestino}&destinoMigracao=true`)
                    .then(res => setNegociadores(listaDe(res))).catch(() => setNegociadores([]));
            })
            .catch(e => avisar(false, e.message || "Erro ao abrir a migração."));
    };

    // Pedido com o CobJur: envia ao suporte; ficha já migrada sem os dados: conclui aqui
    const enviando = aberta?.situacao === "PREENCHER";

    const gravar = () => {
        setSalvando(true);
        setErro("");
        post(`/migracoes-juridico/${aberta.id}/${enviando ? "enviar" : "concluir"}`, {
            pacote: pacoteParaEnvio(pacote),
            usuarioId: usuarioId || null,
            statusFichaId: statusId || null,
            observacao: observacao.trim() || null,
        })
            .then(res => {
                avisar(true, enviando
                    ? `Ficha #${aberta.ficha} enviada ao suporte (solicitação nº ${res?.solicitacao || aberta.solicitacao}). ${res?.resumo || ""}`
                    : `Ficha #${aberta.ficha} concluída. ${res?.resumo || ""}`);
                setAberta(null);
                if (!enviando) avisarFichaAlterada();
                carregar();
            })
            .catch(e => setErro(e.message || (enviando ? "Erro ao enviar ao suporte." : "Erro ao concluir.")))
            .finally(() => setSalvando(false));
    };

    const confirmarRecusa = () => {
        setSalvando(true);
        post(`/migracoes-juridico/${recusando.id}/recusar`, { motivo: motivo.trim() })
            .then(() => {
                avisar(true, `Pedido da ficha #${recusando.ficha} recusado: volta para ${recusando.solicitante || "quem pediu"}, com o motivo.`);
                setRecusando(null);
                carregar();
            })
            .catch(e => avisar(false, e.message || "Erro ao recusar."))
            .finally(() => setSalvando(false));
    };

    const aFazer = situacao === "ABERTAS";

    return (
        <MDBox>
            <Card sx={{ px: 2, py: 1.5, mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4} md={3}>
                        <InputLabel sx={rotulo}>Situação</InputLabel>
                        <MDInput select fullWidth size="small" value={situacao} onChange={e => setSituacao(e.target.value)}>
                            {SITUACOES.map(s => <MenuItem key={s.valor} value={s.valor}>{s.nome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} sm={5} md={5}>
                        <InputLabel sx={rotulo}>Buscar</InputLabel>
                        <MDInput fullWidth size="small" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Devedor, código do cliente, cliente ou nº da ficha" />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                        <InputLabel sx={rotulo}>&nbsp;</InputLabel>
                        <MDButton variant="contained" color="info" size="medium" fullWidth onClick={carregar} disabled={carregando} sx={{ height: 38 }}>
                            {carregando ? <CircularProgress size={16} color="inherit" /> : "Atualizar"}
                        </MDButton>
                    </Grid>
                    <Grid item xs={12}>
                        <MDTypography variant="caption" color="text" display="block" px={1}>
                            Pedidos de migração para a carteira jurídica: preencha o código novo, o débito, o processo, o negociador e o status e
                            envie ao suporte, que conclui a migração (ou devolve para cá, com o motivo). Se o pedido não for para o Jurídico,
                            recuse com o motivo: volta para quem pediu. Fichas que já chegaram ao Jurídico sem os dados são concluídas aqui mesmo.
                            Parado há {dados.diasAviso || 2} dia(s) útil(eis) ou mais fica em vermelho e aparece no sino.
                        </MDTypography>
                    </Grid>
                </Grid>
            </Card>

            <Card sx={{ p: 2 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ display: "table-header-group", backgroundColor: cores.painel }}>
                            <TableRow>
                                {["Ficha", "Devedor", "De → Para", "Pedido", "Situação", aFazer ? "Com" : "O que foi feito", ""].map(t => (
                                    <TableCell key={t}><MDTypography variant="caption" fontWeight="bold" color="text">{t}</MDTypography></TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {itens.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <MDTypography variant="caption" color="text">
                                            {carregando ? "Carregando..." : aFazer ? "Nenhum pedido com o Jurídico." : "Nenhuma migração."}
                                        </MDTypography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {itens.map(i => {
                                const preencher = i.situacao === "PREENCHER";
                                const concluirAqui = i.situacao === "AGUARDANDO";
                                const comJuridico = preencher || concluirAqui;
                                const sit = situacaoDo(i);
                                return (
                                    <TableRow key={i.id} sx={i.parado ? { boxShadow: "inset 3px 0 0 #F44335" } : undefined}>
                                        <TableCell sx={{ verticalAlign: "top" }}>
                                            <MDTypography variant="caption" fontWeight="bold" display="block">#{i.ficha}</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">{i.qtdContratos} débito(s)</MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top" }}>
                                            <MDTypography variant="caption" fontWeight="bold" display="block">{i.devedor}</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {[i.cliente, i.codigoCliente && `Cód. ${i.codigoCliente}`].filter(Boolean).join(" · ")}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top" }}>
                                            <MDTypography variant="caption" color="text" display="block">{i.carteiraOrigem || "-"} → {i.carteiraDestino}</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {[i.responsavel && `Resp.: ${i.responsavel}`, i.status && `Status: ${i.status}`].filter(Boolean).join(" · ")}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", maxWidth: 260 }}>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {i.solicitante ? `Pedido por ${i.solicitante}` : "Migração direta"}
                                                {i.solicitacao ? ` (solicitação nº ${i.solicitacao})` : ""}{i.migradoPor ? `; migrado por ${i.migradoPor}` : ""}
                                            </MDTypography>
                                            {i.observacao && (
                                                <MDTypography variant="caption" color="text" display="block" sx={{ fontStyle: "italic" }}>“{i.observacao}”</MDTypography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", maxWidth: 280 }}>
                                            <Chip size="small" variant="outlined" color={sit.cor} label={sit.texto} sx={{ mb: 0.5 }} />
                                            {comJuridico && (
                                                <>
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {i.devolucao ? `Devolvida em ${dataHora(i.devolucao)}${i.devolvidoPor ? ` por ${i.devolvidoPor}` : ""}` : `Chegou em ${dataHora(i.chegada)}`}
                                                    </MDTypography>
                                                    {i.motivoDevolucao && preencher && (
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontStyle: "italic" }}>“{i.motivoDevolucao}”</MDTypography>
                                                    )}
                                                    {i.parado
                                                        ? <Chip size="small" color="error" variant="outlined" label={`Parada há ${i.diasUteis} dia(s) útil(eis)`} />
                                                        : <MDTypography variant="caption" color="text" display="block">{i.diasUteis} dia(s) útil(eis)</MDTypography>}
                                                </>
                                            )}
                                            {i.situacao === "ENVIADA" && (
                                                <MDTypography variant="caption" color="text" display="block">
                                                    Enviada em {dataHora(i.envioSuporte)}{i.enviadoPor ? ` por ${i.enviadoPor}` : ""}
                                                </MDTypography>
                                            )}
                                            {(i.situacao === "CONCLUIDA" || i.situacao === "RECUSADA") && (
                                                <MDTypography variant="caption" color="text" display="block">
                                                    {dataHora(i.conclusao)}{i.concluidoPor ? ` por ${i.concluidoPor}` : ""}
                                                </MDTypography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", maxWidth: 320 }}>
                                            {comJuridico
                                                ? <MDTypography variant="caption" color="text">{i.atendente ? `${i.atendente} (desde ${dataHora(i.inicioAtendimento)})` : "Ninguém assumiu"}</MDTypography>
                                                : <MDTypography variant="caption" color="text">{i.resumo || "-"}</MDTypography>}
                                        </TableCell>
                                        <TableCell sx={{ ...celulaAcoes, whiteSpace: "nowrap" }} align="right">
                                            <Tooltip title="Abrir a ficha (consulta, numa aba nova)">
                                                <IconButton size="small" color="info" onClick={() => abrirFicha(i)}><Icon fontSize="small">open_in_new</Icon></IconButton>
                                            </Tooltip>
                                            {comJuridico && (
                                                <MDButton variant="outlined" color="info" size="small" sx={{ ml: 1 }} onClick={() => assumir(i)}>
                                                    {i.atendente ? "Assumir de novo" : "Assumir"}
                                                </MDButton>
                                            )}
                                            {preencher && (
                                                <MDButton variant="outlined" color="error" size="small" sx={{ ml: 1 }}
                                                    onClick={() => { setMotivo(""); setRecusando(i); }}>
                                                    Recusar
                                                </MDButton>
                                            )}
                                            {comJuridico && (
                                                <MDButton variant="contained" color="info" size="small" sx={{ ml: 1 }} onClick={() => abrirFormulario(i)}>
                                                    {preencher ? "Preencher e enviar" : "Preencher dados"}
                                                </MDButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <Dialog open={!!aberta} onClose={salvando ? undefined : () => setAberta(null)} maxWidth="md" fullWidth>
                {aberta && (
                    <>
                        <DialogTitle>Dados do Jurídico · Ficha #{aberta.ficha} · {aberta.devedor}</DialogTitle>
                        <DialogContent>
                            <MDTypography variant="caption" color="text" display="block" mb={1}>
                                {aberta.carteiraOrigem || "-"} → {aberta.carteiraDestino} · {aberta.qtdContratos} débito(s)
                                {aberta.solicitante ? ` · pedido por ${aberta.solicitante}` : ""}
                                {aberta.observacao ? `: “${aberta.observacao}”` : ""}
                            </MDTypography>
                            {aberta.motivoDevolucao && enviando && (
                                <MDBox mb={1.5} px={1.5} py={1} sx={{ borderRadius: 1, border: `1px solid ${cores.borda}`, backgroundColor: cores.painel }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="warning" display="block">
                                        {`Devolvida pelo suporte${aberta.devolvidoPor ? ` por ${aberta.devolvidoPor}` : ""}${aberta.devolucao ? ` em ${dataHora(aberta.devolucao)}` : ""}`}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text" display="block">{aberta.motivoDevolucao}</MDTypography>
                                </MDBox>
                            )}
                            {(aberta.debitos || []).length > 0 && (
                                <MDBox mb={1.5}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block" px={1} mb={0.5}>
                                        {enviando ? "Débitos do pedido" : "Débitos que vieram na migração"}
                                    </MDTypography>
                                    <Table size="small">
                                        <TableBody>
                                            {aberta.debitos.map((d, k) => (
                                                <TableRow key={k}>
                                                    <TableCell sx={{ py: 0.5 }}><MDTypography variant="caption" color="text">{d.titulo || "(sem título)"}</MDTypography></TableCell>
                                                    <TableCell sx={{ py: 0.5 }}><MDTypography variant="caption" color="text">Venc. {d.vencimento || "-"}</MDTypography></TableCell>
                                                    <TableCell sx={{ py: 0.5 }}><MDTypography variant="caption" color="text">Valor {moeda(d.valor)}</MDTypography></TableCell>
                                                    <TableCell sx={{ py: 0.5 }}><MDTypography variant="caption" color="text">Saldo {moeda(d.saldo)}</MDTypography></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </MDBox>
                            )}
                            <PacoteJuridicoMigracao dados={aberta.dadosCodigo} valor={pacote} onChange={setPacote} carteiraId={aberta.idCarteiraDestino}
                                ajuda={enviando
                                    ? "Vai para o suporte com o pedido e é aplicado na ficha quando ele aprovar a migração. Zerar os débitos vale para os do pedido."
                                    : "Aplicado na ficha ao concluir. Zerar os débitos vale para os que vieram nesta migração."} />
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={rotulo}>Negociador</InputLabel>
                                    <MDInput select fullWidth size="small" value={usuarioId} onChange={e => setUsuarioId(e.target.value)} SelectProps={{ displayEmpty: true }}>
                                        <MenuItem value="">Mantém o atual{aberta.responsavel ? ` (${aberta.responsavel})` : ""}</MenuItem>
                                        {negociadores.map(u => <MenuItem key={u.id || u.cvId} value={u.id || u.cvId}>{u.nome || u.ccNome}</MenuItem>)}
                                    </MDInput>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={rotulo}>Status da ficha</InputLabel>
                                    <MDInput select fullWidth size="small" value={statusId} onChange={e => setStatusId(e.target.value)} SelectProps={{ displayEmpty: true }}>
                                        <MenuItem value="">Mantém o atual{aberta.status ? ` (${aberta.status})` : ""}</MenuItem>
                                        {statusFicha.map(st => <MenuItem key={st.id || st.cvId} value={st.id || st.cvId}>{st.nome || st.ccNome}</MenuItem>)}
                                    </MDInput>
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel sx={rotulo}>{enviando ? "Observação para o suporte" : "Observação (vai na ocorrência da ficha)"}</InputLabel>
                                    <MDInput fullWidth multiline rows={2} value={observacao} onChange={e => setObservacao(e.target.value)} inputProps={{ maxLength: 1000 }} />
                                </Grid>
                                {erro && (
                                    <Grid item xs={12}>
                                        <MDTypography variant="caption" color="error" display="block" px={1}>{erro}</MDTypography>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <MDButton variant="text" color="secondary" onClick={() => setAberta(null)} disabled={salvando}>Cancelar</MDButton>
                            <MDButton variant="gradient" color="info" onClick={gravar} disabled={salvando}>
                                {salvando ? <CircularProgress size={18} color="inherit" /> : enviando ? "Enviar ao suporte" : "Concluir"}
                            </MDButton>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={!!recusando} onClose={salvando ? undefined : () => setRecusando(null)} maxWidth="sm" fullWidth>
                {recusando && (
                    <>
                        <DialogTitle>Recusar o pedido · Ficha #{recusando.ficha}</DialogTitle>
                        <DialogContent>
                            <MDTypography variant="caption" color="text" display="block" mb={1}>
                                O pedido volta para {recusando.solicitante || "quem pediu"}, com o motivo; a ficha continua na carteira
                                {recusando.carteiraOrigem ? ` ${recusando.carteiraOrigem}` : " de origem"} e volta a poder negociar.
                            </MDTypography>
                            <InputLabel sx={rotulo}>Motivo da recusa</InputLabel>
                            <MDInput fullWidth multiline rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} inputProps={{ maxLength: 2000 }} autoFocus />
                        </DialogContent>
                        <DialogActions>
                            <MDButton variant="text" color="secondary" onClick={() => setRecusando(null)} disabled={salvando}>Cancelar</MDButton>
                            <MDButton variant="gradient" color="error" onClick={confirmarRecusa} disabled={salvando || motivo.trim().length < 5}>
                                {salvando ? <CircularProgress size={18} color="inherit" /> : "Recusar"}
                            </MDButton>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <MDSnackbar
                color={aviso.ok ? "success" : "error"}
                icon={aviso.ok ? "check" : "warning"}
                title={aviso.ok ? "Sucesso" : "Atenção"}
                content={aviso.texto}
                dateTime=""
                open={aviso.aberto}
                onClose={() => setAviso(a => ({ ...a, aberto: false }))}
                close={() => setAviso(a => ({ ...a, aberto: false }))}
                bgWhite
            />
        </MDBox>
    );
}
